






import VueApexCharts from "vue-apexcharts";
import { Vue, Component, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import {
  TimelineChartData,
  TimelineChartSeries
} from "../../models/timeline-chart-data";
import { invoiceAnalyticsService } from "../../services/analytics.service";
import RecognitionAnalyticsStore from "../../store/recognition-analytics.store";

@Component({
  components: { apexchart: VueApexCharts }
})
export default class RecogTimelineChart extends Vue {
  private readonly recognitionStore: RecognitionAnalyticsStore = getModule(
    RecognitionAnalyticsStore,
    this.$store
  );
  get recogHistoricalData() {
    return this.recognitionStore.getRecogHistoricalData;
  }
  get dateRange() {
    return this.recognitionStore.getDateRange;
  }
  get selectedSite() {
    return this.recognitionStore.getSelectedSite;
  }
  get searchButtonClicked() {
    return this.recognitionStore.getSearchButtonClicked;
  }
  get includeVsInRecogRates() {
    return this.recognitionStore.getIncludeVsInRecogRates;
  }
  private series: TimelineChartSeries[] = [];
  private chartOptions = {
    chart: {
      fontFamily: "'Lato', sans-serif",
      toolbar: {
        export: {
          csv: {
            filename: `daily-average-recognition-${this.dateRange.startDate}-to-${this.dateRange.endDate}`,
            columnDelimiter: ",",
            headerCategory: "Entered Date"
          }
        }
      }
    },
    grid: {
      padding: {
        left: 25,
        right: 25
      }
    },
    xaxis: {
      type: "category",
      labels: {
        formatter: (value: string) => {
          if (value) {
            return value.substring(0, 10);
          }
        }
      },
      title: {
        text: "Entered Date"
      }
    },
    yaxis: [
      {
        title: {
          text: "Recognition Rate (%)"
        },
        labels: {
          formatter: function(value: number) {
            if (value) {
              return Math.floor(value);
            }
          }
        },
        min: 0,
        max: 100
      },
      {
        opposite: true,
        title: {
          text: "Number of Fields Analyzed"
        },
        labels: {
          formatter: function(value: number) {
            if (value) {
              return value.toFixed().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
            }
          }
        }
      }
    ],
    dataLabels: {
      enabled: false
    },
    states: {
      normal: {
        filter: {
          type: "desaturate"
        }
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: {
          type: "darken",
          value: 0.25
        }
      },
      hover: {
        filter: {
          type: "darken",
          value: 0.25
        }
      }
    },
    stroke: {
      curve: "smooth",
      width: [2, 0]
    },
    title: {
      text: "Daily Average Recognition Rate",
      align: "left"
    },
    subtitle: {
      text: "Daily average across all fields",
      align: "left"
    },
    tooltip: {
      x: {
        format: "MMMM d yyyy"
      },
      y: [
        {
          formatter: function(value: any) {
            if (value) {
              return value.toString().concat("%");
            }
          }
        },
        {
          formatter: function(value: number) {
            if (value) {
              return value.toFixed().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
            }
          }
        }
      ]
    },
    noData: {
      text: "No data for selected options",
      align: "center"
    }
  };

  async created() {
    if (Object.keys(this.recogHistoricalData).length === 0) {
      await this.fetchNewChartData();
    }
    this.updateChart();
    this.$emit("chartLoaded");
  }

  async fetchNewChartData() {
    if (this.selectedSite !== "") {
      const chartData: {
        [key: string]: TimelineChartData[];
      } = await invoiceAnalyticsService.loadHistoricalRecogDataAsync(
        this.selectedSite,
        this.dateRange.startDate,
        this.dateRange.endDate,
        "",
        "",
        this.includeVsInRecogRates
      );
      this.recognitionStore.updateRecogHistoricalData(chartData);
    } else {
      this.recognitionStore.updateRecogHistoricalData({
        recogRate: [],
        sampleSize: []
      });
    }
  }

  updateChart() {
    this.series = [
      {
        name: "Daily recognition rate",
        data: this.recogHistoricalData.recogRate,
        type: "line",
        color: "#D06C49"
      },
      {
        name: "Fields used to calculate rate",
        data: this.recogHistoricalData.sampleSize,
        type: "bar",
        color: "#d3d3d3"
      }
    ];
  }

  @Watch("searchButtonClicked")
  async updateSearch() {
    await this.fetchNewChartData();
    this.updateChart();
    this.$emit("chartLoaded");
  }
}
